<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { notificationSettingsApi, type NotificationSettingData } from '@/api/entities/regle/notificationSettings'

definePage({
  meta: {
    title: '알림 설정',
    action: 'manage',
    subject: 'NotificationSetting',
  },
})

const { snackbar, handleError } = useErrorHandler()

const loading = ref(false)
const saving = ref(false)
const testingChannel = ref<string | null>(null)

const settings = ref<NotificationSettingData>({
  id: 0,
  auto_notifications: true,
  daily_report_time: null,
  email_recipients: null,
  slack_webhook_url: null,
  alimtalk_enabled: true,
  email_enabled: false,
  slack_enabled: false,
})

const loadSettings = async () => {
  loading.value = true
  try {
    const { success, data, error } = await notificationSettingsApi.get()
    if (success && data)
      settings.value = data
    else if (error)
      handleError(error, { notificationMessage: '설정을 불러오는데 실패했습니다.' })
  }
  catch (error) {
    handleError(error, { notificationMessage: '설정을 불러오는데 실패했습니다.' })
  }
  finally {
    loading.value = false
  }
}

const saveSettings = async () => {
  saving.value = true
  try {
    const { success, data, error } = await notificationSettingsApi.update(settings.value)
    if (success && data) {
      settings.value = data
      snackbar.success('설정이 저장되었습니다.')
    }
    else if (error) {
      handleError(error, { notificationMessage: '저장에 실패했습니다.' })
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '저장에 실패했습니다.' })
  }
  finally {
    saving.value = false
  }
}

const sendTest = async (channel: 'email' | 'slack' | 'alimtalk') => {
  testingChannel.value = channel
  try {
    const { success, data, error } = await notificationSettingsApi.test(channel)
    if (success && data) {
      if (data.success)
        snackbar.success(data.message)
      else
        snackbar.error(data.message)
    }
    else if (error) {
      handleError(error, { notificationMessage: '테스트 발송에 실패했습니다.' })
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '테스트 발송에 실패했습니다.' })
  }
  finally {
    testingChannel.value = null
  }
}

onMounted(loadSettings)
</script>

<template>
  <VRow>
    <VCol
      cols="12"
      md="6"
    >
      <VCard :loading="loading">
        <VCardTitle>일반 설정</VCardTitle>
        <VCardText>
          <VSwitch
            v-model="settings.auto_notifications"
            label="자동 알림 활성화"
            color="primary"
            hide-details
            class="mb-4"
          />
          <VTextField
            v-model="settings.daily_report_time"
            label="일일 리포트 발송 시간"
            placeholder="09:00"
            hint="HH:MM 형식"
            persistent-hint
            variant="outlined"
            density="compact"
          />
        </VCardText>
      </VCard>
    </VCol>

    <VCol
      cols="12"
      md="6"
    >
      <VCard>
        <VCardTitle>채널 설정</VCardTitle>
        <VCardText>
          <VSwitch
            v-model="settings.alimtalk_enabled"
            label="알림톡"
            color="primary"
            hide-details
            class="mb-2"
          />
          <VSwitch
            v-model="settings.email_enabled"
            label="이메일"
            color="primary"
            hide-details
            class="mb-2"
          />
          <VSwitch
            v-model="settings.slack_enabled"
            label="Slack"
            color="primary"
            hide-details
          />
        </VCardText>
      </VCard>
    </VCol>

    <VCol
      cols="12"
      md="6"
    >
      <VCard>
        <VCardTitle>수신자 설정</VCardTitle>
        <VCardText>
          <VTextarea
            v-model="settings.email_recipients"
            label="이메일 수신자"
            placeholder="admin@example.com, user@example.com"
            hint="쉼표로 구분하여 입력"
            persistent-hint
            variant="outlined"
            density="compact"
            rows="3"
            class="mb-4"
          />
          <VTextField
            v-model="settings.slack_webhook_url"
            label="Slack Webhook URL"
            placeholder="https://hooks.slack.com/services/..."
            variant="outlined"
            density="compact"
          />
        </VCardText>
      </VCard>
    </VCol>

    <VCol
      cols="12"
      md="6"
    >
      <VCard>
        <VCardTitle>테스트 발송</VCardTitle>
        <VCardText>
          <div class="d-flex gap-2 flex-wrap">
            <VBtn
              color="primary"
              variant="outlined"
              :loading="testingChannel === 'email'"
              :disabled="!settings.email_enabled || testingChannel !== null"
              @click="sendTest('email')"
            >
              이메일 테스트
            </VBtn>
            <VBtn
              color="primary"
              variant="outlined"
              :loading="testingChannel === 'slack'"
              :disabled="!settings.slack_enabled || testingChannel !== null"
              @click="sendTest('slack')"
            >
              Slack 테스트
            </VBtn>
          </div>
        </VCardText>
      </VCard>
    </VCol>

    <VCol cols="12">
      <div class="d-flex justify-end">
        <VBtn
          color="primary"
          :loading="saving"
          @click="saveSettings"
        >
          설정 저장
        </VBtn>
      </div>
    </VCol>
  </VRow>
</template>
